import '@banno/jha-wc/src/sliders';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { Holiday, HolidaysService } from '@treasury/domain/dates';
import { Loan } from '@treasury/domain/loans';
import { LoanService } from '@treasury/domain/loans/loan.service';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { TmDetailedError } from '@treasury/domain/shared';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { arrowBackIcon } from '@treasury/presentation/assets/icons';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-currency-field';
import '@treasury/presentation/components/tm-date-picker';
import '@treasury/presentation/components/tm-fdic-digital-sign';
import '@treasury/presentation/components/tm-labeled-list';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-select';
import { formatUsdCurrency } from '@treasury/utils/functions/currency.helpers';
import { GridColumnBodyLitRenderer, columnBodyRenderer } from '@vaadin/grid/lit.js';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
    DatePickerDate,
    inactiveHolidays,
    inactiveWeekends,
} from '../../../utilities/disabled-date.functions';
import { classMap } from 'lit/directives/class-map.js';

export const tagName = 'loan-payment-widget';
@customElement(tagName)
export class LoanPaymentWidget extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = false;

    @InjectProperty()
    declare private loanService: LoanService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private deviceService: DeviceService;

    @DeepReactive()
    private loanPayment = new Loan();

    @InjectProperty()
    declare private holidaysService: HolidaysService;

    @state()
    private loanPaymentFromAccounts: CompanyAccount[] = [];

    @state()
    private loanPaymentFromAccountOptions: { value: CompanyAccount; label: string }[] = [];

    @state()
    private selectedFromAccount: CompanyAccount | undefined;

    @state()
    private loanPaymentToAccounts: CompanyAccount[] = [];

    @state()
    private loanPaymentToAccountOptions: { value: CompanyAccount; label: string }[] = [];

    @state()
    private selectedToAccount: CompanyAccount | undefined;

    @state()
    private amount: string | undefined;

    @state()
    private paymentDate: string | undefined;

    @state()
    private holidays: Array<Holiday> = [];

    @state()
    private confirming = false;

    @state()
    private hideHeader = false;

    private get headerText() {
        const header = this.confirming ? 'Quick Loan Payment Successful' : 'Quick Loan Payment';
        return this.hideHeader ? undefined : header;
    }

    private get isDesktop() {
        return this.deviceService.isDesktop;
    }

    private get subHeaderText() {
        return this.confirming ? undefined : 'Regular Payment';
    }

    protected async firstUpdated() {
        this.loading = true;
        Promise.all([this.getFromAccounts(), this.getToAccounts()]).then(() => {
            this.loading = false;
        });
    }

    protected render() {
        return html`
            <tm-card .header=${this.headerText} .subheader=${this.subHeaderText}>
                ${this.loading ? html`<tm-loader card></tm-loader>` : nothing}
                ${!this.loading && !this.confirming
                    ? html`<jha-slider class="-mx-4">
                          <jha-slider-content .staticHeight=${true} .fullHeight=${true}>
                              <jha-slider-pane pane-id="from-accounts-list">
                                  <div class="ml-4">
                                      <div class="flex justify-center items-center relative my-3">
                                          <tm-icon
                                              class="absolute left-0"
                                              .icon=${arrowBackIcon}
                                              @click=${() => {
                                                  this.changePane('index');
                                              }}
                                          ></tm-icon>
                                          <span class="font-medium">Select from account</span>
                                      </div>
                                      ${this.renderFromAccountsList()}
                                  </div>
                              </jha-slider-pane>
                              <jha-slider-pane pane-id="to-accounts-list">
                                  <div class="ml-4">
                                      <div class="flex justify-center items-center relative my-3">
                                          <tm-icon
                                              class="absolute left-0"
                                              .icon=${arrowBackIcon}
                                              @click=${() => {
                                                  this.changePane('index');
                                              }}
                                          ></tm-icon>
                                          <span class="font-medium">Select to loan account</span>
                                      </div>
                                      ${this.renderToAccountsList()}
                                  </div>
                              </jha-slider-pane>
                              <jha-slider-pane index focus>
                                  <tm-form class="">
                                      <tm-fdic-digital-sign
                                          container="py-2"
                                          class="ml-4"
                                          .reversed=${this.theme === 'dark'}
                                      ></tm-fdic-digital-sign>
                                      <tm-form-row
                                          required
                                          noWrap
                                          .label=${'From'}
                                          @click=${() => {
                                              this.changePane('from-accounts-list');
                                          }}
                                      >
                                          <tm-slider
                                              no-sheet
                                              .value=${this.renderAccount(
                                                  this.loanPayment.fromAccount
                                              )}
                                              .placeholder=${'Select account'}
                                          >
                                          </tm-slider
                                      ></tm-form-row>
                                      <tm-form-row
                                          required
                                          noWrap
                                          .label=${'To loan'}
                                          @click=${() => {
                                              this.changePane('to-accounts-list');
                                          }}
                                      >
                                          <tm-slider
                                              no-sheet
                                              .value=${this.renderAccount(
                                                  this.loanPayment.toAccount
                                              )}
                                              .placeholder=${'Select account'}
                                          >
                                          </tm-slider>
                                      </tm-form-row>
                                      <tm-form-row required label="Amount">
                                          <tm-currency-field
                                              class="max-w-[130px] my-2"
                                              .initial=${this.amount}
                                              .required=${true}
                                              @value-changed=${({
                                                  detail,
                                              }: TmValueChanged<string>) => {
                                                  if (detail.value) {
                                                      this.amount = detail.value;
                                                      this.loanPayment.amount = parseFloat(
                                                          this.amount
                                                      );
                                                  }
                                              }}
                                          ></tm-currency-field>
                                      </tm-form-row>
                                      <tm-form-row required label="Payment date" noWrap>
                                          <tm-date-picker
                                              class="max-w-[150px]"
                                              .required=${true}
                                              .isDateDisabled=${(date: DatePickerDate) =>
                                                  inactiveWeekends(date) ||
                                                  inactiveHolidays(date, this.holidays)}
                                              .initial=${new Date()}
                                              @value-changed=${(e: TmValueChanged<Date>) => {
                                                  const { value, isValid } = e.detail;
                                                  if (!isValid || !value) {
                                                      return;
                                                  }
                                                  this.paymentDate = value.toDateString();
                                                  this.loanPayment.transferDate =
                                                      value.toDateString();
                                              }}
                                          ></tm-date-picker>
                                      </tm-form-row>
                                  </tm-form>
                                  <div
                                      class=${classMap({
                                          'flex w-full items-center justify-around pt-4': true,
                                          'flex-col': this.isDesktop,
                                      })}
                                  >
                                      <tm-button
                                          importance="secondary"
                                          .size=${this.isDesktop ? 'normal' : 'small'}
                                          .disabled=${this.loading ||
                                          !this.selectedFromAccount ||
                                          !this.selectedToAccount ||
                                          !Number(this.amount) ||
                                          !this.paymentDate}
                                          @click=${() => {
                                              this.submitLoanPayment();
                                          }}
                                          >Make Loan Payment</tm-button
                                      >
                                      <tm-button
                                          importance="tertiary"
                                          .size=${this.isDesktop ? 'normal' : 'small'}
                                          @click=${() => {
                                              this.navigationService.navigate(
                                                  `loans/payment/create/new`
                                              );
                                          }}
                                          >Advanced loan options</tm-button
                                      >
                                  </div></jha-slider-pane
                              ></jha-slider-content
                          ></jha-slider
                      >`
                    : nothing}
                ${this.confirming
                    ? html`<div>
                          <h2 class="text-lg font-medium">Loan Payment</h2>
                          <h1 class="text-2xl">${this.loanPayment.amountFormatted}</h1>
                          <div class="grid grid-cols-[auto_1fr] gap-x-3 my-4 text-sm">
                              <div class="text-left mr-3 py-1 text-secondary">From</div>
                              <div class="text-left py-1 text-primary">
                                  ${this.loanPayment.fromAccount?.name}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">To</div>
                              <div class="text-left py-1 text-primary">
                                  ${this.loanPayment.toAccount?.name}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Frequency</div>
                              <div class="text-left py-1 text-primary">One Time</div>
                              <div class="text-left mr-3 py-1 text-secondary">Status</div>
                              <div class="text-left py-1 text-primary">
                                  ${this.loanPayment.statusFormatted}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Payment Date</div>
                              <div class="text-left py-1 text-primary">
                                  ${this.loanPayment.transferDateFormatted}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Memo</div>
                              <div class="text-left py-1">
                                  ${this.loanPayment.memo
                                      ? html`<span class="text-primary"
                                            >${this.loanPayment.memo}</span
                                        >`
                                      : html`<span class="text-tertiary">none</span>`}
                              </div>
                              <div class="text-left mr-3 py-1 text-secondary">Transaction ID</div>
                              <div class="text-left py-1 text-primary">
                                  ${this.loanPayment.transactionId}
                              </div>
                          </div>
                          <div class="flex w-full items-center justify-center">
                              <tm-button
                                  importance="secondary"
                                  @click=${() => {
                                      this.confirming = false;
                                      this.loanPayment = new Loan();
                                      this.amount = '0';
                                  }}
                                  >Done</tm-button
                              >
                          </div>
                      </div>`
                    : nothing}
            </tm-card>
        `;
    }

    protected async getFromAccounts() {
        this.loanPaymentFromAccounts = await this.loanService.getLoanPaymentFromAccounts();
        this.loanPaymentFromAccountOptions = this.loanPaymentFromAccounts.map(account => ({
            value: account,
            label: account.name,
        }));
        this.holidays = await this.holidaysService.getHolidays();
    }

    protected async getToAccounts() {
        this.loanPaymentToAccounts = await this.loanService.getLoanPaymentToAccounts(
            this.selectedFromAccount?.id?.toString() || ''
        );
        this.loanPaymentToAccountOptions = this.loanPaymentToAccounts.map(account => ({
            value: account,
            label: account.name,
        }));
    }

    protected async submitLoanPayment() {
        this.loading = true;
        try {
            const response = await this.loanService.createLoanPayment(this.loanPayment);
            if (response.transactions?.length) {
                this.loanPayment.transactionId = response.transactions[0].transactionId;
                this.loanPayment.status = response.transactions[0].status;
            }
            this.dispatchEvent(new CustomEvent('loan-payment-success'));
            this.notificationService.renderSuccess('Loan payment successful');
            this.confirming = true;
        } catch (e) {
            this.notificationService.renderError(e as TmDetailedError);
        } finally {
            this.loading = false;
        }
    }

    private changePane(paneId: string) {
        this.hideHeader = paneId === 'from-accounts-list' || paneId === 'to-accounts-list';
        const sliderContent = this.shadowRoot?.querySelector('jha-slider-content');
        if (sliderContent) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (sliderContent as any).focusedPane = paneId;
        }
    }

    private renderAccount(account: CompanyAccount) {
        const amount = account.type === 'Loan' ? account.currentBalance : account.availableBalance;
        if (account.accountDisplayLabel) {
            return html`<div class="flex flex-col items-end">
                <div class="text-sm text-action">${account.accountDisplayLabel}</div>
                <div class="text-xs text-secondary">${formatUsdCurrency(amount || 0)}</div>
            </div>`;
        } else {
            return undefined;
        }
    }

    private renderFromAccountsList() {
        const renderAccount = (item: CompanyAccount) =>
            html`<div class="py-2 pr-4">
                <div class="flex flex-row justify-between">
                    <div class="flex flex-col">
                        <div class="text-sm">${item.name}</div>
                        <div class="text-xs text-secondary">${item.accountDisplayLabel}</div>
                    </div>
                    <div class="flex flex-col text-right">
                        <div class="text-sm">${formatUsdCurrency(item.availableBalance || 0)}</div>
                        <div class="text-xs text-secondary">Available</div>
                    </div>
                </div>
            </div>`;
        return html`<vaadin-grid
            .items=${this.loanPaymentFromAccounts}
            @active-item-changed=${(e: CustomEvent) => {
                if (!e.detail.value) {
                    return;
                }
                this.selectedFromAccount = e.detail.value;
                this.loanPayment.fromAccount = e.detail.value;
                this.getToAccounts();
                this.changePane('index');
            }}
            ><vaadin-grid-column
                ${columnBodyRenderer(renderAccount as GridColumnBodyLitRenderer<CompanyAccount>)}
            ></vaadin-grid-column
            ></vaadin-grid-column
        ></vaadin-grid>`;
    }

    private renderToAccountsList() {
        const renderAccount = (item: CompanyAccount) =>
            html`<div class="py-2 pr-4">
                <div class="flex flex-row justify-between">
                    <div class="flex flex-col">
                        <div class="text-sm">${item.name}</div>
                        <div class="text-xs text-secondary">${item.number} | ${item.status}</div>
                    </div>
                    <div class="flex flex-col text-right">
                        <div class="text-sm">${formatUsdCurrency(item.currentBalance || 0)}</div>
                        <div class="text-xs text-secondary">Current</div>
                    </div>
                </div>
            </div>`;
        return html`<vaadin-grid
            .items=${this.loanPaymentToAccounts}
            @active-item-changed=${(e: CustomEvent) => {
                if (!e.detail.value) {
                    return;
                }
                this.selectedToAccount = e.detail.value;
                this.loanPayment.toAccount = e.detail.value;
                this.amount = this.selectedToAccount?.loanPaymentAmountDue?.toString();
                this.changePane('index');
            }}
            ><vaadin-grid-column
                ${columnBodyRenderer(renderAccount as GridColumnBodyLitRenderer<CompanyAccount>)}
            ></vaadin-grid-column
            >/vaadin-grid></vaadin-grid
        >`;
    }

    public static get styles() {
        return css`
            :host {
                display: block;
                --tm-card-min-height: 100px;
            }
            vaadin-grid,
            tm-loader {
                height: 285px;
            }
            vaadin-grid-cell-content {
                padding: 0;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'loan-payment-widget': LoanPaymentWidget;
    }
}
